import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import ReactToPrint from "react-to-print";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Label
} from "reactstrap";
import moment from "moment";
import LottieLoader from '../../LottieLoader';
import lottieEmptyFile from '../../../files/animation/empty_new.json';
import lottieLoaderFile from '../../../files/animation/loading.json';
import ViewRPMFormLogs from "./ViewRPMFormLogs";
import AddRPMCarePlan from "./AddRPMCarePlan";
import ViewRPMCarePlan from "./ViewRPMCarePlan";
import RPMCarePlanPrint from "./RPMCarePlanPrint";

// Constants
const RPM_CARE_PLAN_TITLE = "RPM Care Plan";
const ADD_RPM_CARE_PLAN_TITLE = "Add RPM Care Plan";
const ICON_MEDICAL_BAG = "mdi mdi-medical-bag";
const ICON_CLIPBOARD_PLUS = "mdi mdi-clipboard-plus";
const ICON_EYE = "mdi mdi-eye";
const CLASS_ADD_RPM_FORM = "add-ccm-form";
const CLASS_CLOSE_BUTTON = "close_button";

const RPMCarePlan = (props) => {
  const dispatch = useDispatch();

  // Redux state using useSelector
  const rpmCarePlanList = useSelector((state) => state.careprovider?.rpmCarePlanList || []);
  const ongoingRPMCarePlanListRequest = useSelector((state) => state.careprovider?.ongoingRPMCarePlanListRequest || false);

  // Local state using useState
  const [modelViewRPMFormLogs, setModelViewRPMFormLogs] = useState(false);
  const [showAddRPMModal, setShowAddRPMModal] = useState(false);
  const [showViewRPMModal, setShowViewRPMModal] = useState(false);
  const [selectedRPMCarePlan, setSelectedRPMCarePlan] = useState(null);
  const [isEditingRPM, setIsEditingRPM] = useState(false);
  
  // Ref for ViewRPMCarePlan to call save function
  const viewRPMRef = React.useRef(null);
  const prevShowAddRPMModal = useRef(false);
  const prevShowViewRPMModal = useRef(false);

  // Clear RPM data when opening Add modal to prevent stale data
  useEffect(() => {
    if (showAddRPMModal && !prevShowAddRPMModal.current) {
      dispatch({ type: "CLEAR_RPM_PATIENT_CONDITIONS" });
      dispatch({ type: "CLEAR_RPM_PATIENT_EXPECTATIONS" });
      dispatch({ type: "CLEAR_RPM_MEDICAL_NECESSITY_CONFIG" });
    }
    prevShowAddRPMModal.current = showAddRPMModal;
  }, [showAddRPMModal, dispatch]);

  // Clear RPM data when opening View modal to prevent stale data
  useEffect(() => {
    if (showViewRPMModal && !prevShowViewRPMModal.current) {
      dispatch({ type: "CLEAR_RPM_PATIENT_CONDITIONS" });
      dispatch({ type: "CLEAR_RPM_PATIENT_EXPECTATIONS" });
      dispatch({ type: "CLEAR_RPM_MEDICAL_NECESSITY_CONFIG" });
    }
    prevShowViewRPMModal.current = showViewRPMModal;
  }, [showViewRPMModal, dispatch]);

  // componentDidMount and componentDidUpdate combined using useEffect
  useEffect(() => {
    const patientId = (props.profile && props.profile._id) || props.userId;
    if (patientId) {
      dispatch({
        type: "DO_FETCH_RPM_CARE_PLAN_LIST",
        payload: { patientId }
      });
    }
  }, [dispatch, props.profile, props.userId]);

  // Toggle functions
  const toggleViewRPMModal = () => {
    setShowViewRPMModal(!showViewRPMModal);
    // Clear selected when closing
    if (showViewRPMModal) {
      setSelectedRPMCarePlan(null);
      setIsEditingRPM(false);
    }
  };

  const toggleViewRPMFormLogs = () => {
    setModelViewRPMFormLogs(!modelViewRPMFormLogs);
  };

  const toggleAddRPMModal = () => {
    setShowAddRPMModal(!showAddRPMModal);
  };

  // This callback is passed to child to capture the form submit function
  // Use ref to store the callback to avoid triggering effects on prop changes
  const submitFormRef = React.useRef(null);

  const handleSubmitForm = useCallback((submitForm) => {
    submitFormRef.current = submitForm;
  }, []);

  const handleSaveRPMCarePlan = () => {
    if (submitFormRef.current) {
      submitFormRef.current();
    }
  };

  // Handle update button click - switch to edit mode
  const handleUpdateRPMCarePlan = () => {
    setIsEditingRPM(true);
  };

  // Handle save button click
  const handleSaveRPMCarePlanEdit = () => {
    // Call the save function from ViewRPMCarePlan via ref
    if (viewRPMRef.current) {
      const saveResult = viewRPMRef.current.save();
      // If save returns false, validation failed - don't close modal
      if (saveResult === false) {
        return;
      }
    }
    // Close the view modal after saving
    toggleViewRPMModal();
    setIsEditingRPM(false);
  };

  const { 
    rpmSubmissinLogs, 
    userId, 
    toggleAddRPMForm,
    profile
  } = props;

  return (
    <>
      <div className="care-plan-header-background ">
        <Row>
          <Col sm="8" className="center-label remove-table-padding">
            <h3 className="text-center title-device" style={{ marginLeft: 25 }}>
              <i className={ICON_MEDICAL_BAG}></i> {RPM_CARE_PLAN_TITLE}
            </h3>
          </Col>
          <Col sm="4" className="float-right">
            <Button
              className={`btn btn-info ml-2 ${CLASS_ADD_RPM_FORM}`}
              style={{ float: 'right' }}
              onClick={() => {
                toggleAddRPMModal();
              }}
            >
              <i className={ICON_CLIPBOARD_PLUS}></i>
              {ADD_RPM_CARE_PLAN_TITLE}
            </Button>
          </Col>
        </Row>
      </div>
      <div className="scrollbar-table-cls">
        {ongoingRPMCarePlanListRequest ? (
          <div className="text-center" style={{ marginTop: "50px", marginBottom: "50px" }}>
            <LottieLoader
              loadLoop={true}
              autoplayLoop={true}
              source={lottieLoaderFile}
              Lheight={100}
              Lwidth={100}
            />
          </div>
        ) : (
          <BootstrapTable
          wrapperClasses="table-responsive"
          keyField="_id"
          bordered={true}
          hover={true}
          condensed={true}
          noDataIndication={() => (
            <div>
              <LottieLoader
                loadLoop={false}
                autoplayLoop={true}
                source={lottieEmptyFile}
                Lheight={150}
                Lwidth={150}
                titleSmall={"Sorry! No RPM care plan found :("}
                subTitleSmall={"We can't find any RPM care plan."}
              />
            </div>
          )}
          data={rpmCarePlanList || []}
          columns={[
            {
              dataField: "description",
              text: "Created By",
              style: { textAlign: "left", verticalAlign: "middle" },
              formatter: (cell, row) => {
                if (!row.createdBy) {
                  return <div className="text-left"> - </div>;
                }
                return (
                  <div className="text-left">{row.createdBy.fname}{" "} {row.createdBy.lname}</div>
                );
              },
              headerStyle: (colum, colIndex) => {
                return { textAlign: "left", padding: "16px", fontSize: "16px", color: "#070707" };
              },
            },
            {
              dataField: "description",
              text: "Modify By",
              style: { textAlign: "left", verticalAlign: "middle" },
              formatter: (cell, row) => {
                if (!row.modifiedBy) {
                  return <div className="text-left"> Initial Form </div>;
                }
                return (
                  <div className="text-left">{row.modifiedBy.fname}{" "} {row.modifiedBy.lname}</div>
                );
              },
              headerStyle: (colum, colIndex) => {
                return { textAlign: "left", padding: "16px", fontSize: "16px", color: "#070707" };
              },
            },
            {
              dataField: "createdAt",
              text: "Created Date",
              style: { textAlign: "left", verticalAlign: "middle" },
              formatter: (cell, row) => {
                if (row.createdAt) {
                  return moment(row.createdAt).tz(moment.tz.guess()).format("MMMM DD, YYYY hh:mm:ss A");
                } else {
                  return "NONE";
                }
              },
              headerStyle: (colum, colIndex) => {
                return { textAlign: "left", padding: "16px", fontSize: "16px", color: "#070707" };
              },
            },
            {
              dataField: "description",
              text: "Last Modify Date",
              style: { textAlign: "left", verticalAlign: "middle" },
              formatter: (cell, row) => {
                if (row.lastModifyDate) {
                  return moment(row.lastModifyDate).tz(moment.tz.guess()).format("MMMM DD, YYYY hh:mm:ss A");
                } else {
                  return "NONE";
                }
              },
              headerStyle: (colum, colIndex) => {
                return { textAlign: "left", padding: "16px", fontSize: "16px", color: "#070707" };
              },
            },
            {
              text: "Action",
              style: { textAlign: "left", verticalAlign: "middle" },
              headerStyle: (colum, colIndex) => {
                return { width: "200px", padding: "16px", fontSize: "16px", color: "#070707" };
              },
              formatter: (cell, row) => {
                return (
                  <div className="text-center">
                    <Button
                      type="button"
                      className={`btn btn-info ml-2 ${CLASS_ADD_RPM_FORM}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedRPMCarePlan(row);
                        setShowViewRPMModal(true);                        
                      }}
                    >
                      <i className={ICON_EYE}></i>&nbsp;View
                    </Button>
                  </div>
                );
              }
            }
          ]}
        />
        )}
      </div>

      {/* View RPM Form Logs Modal */}
      {modelViewRPMFormLogs && userId && rpmSubmissinLogs && rpmSubmissinLogs.length > 0 && (
        <ViewRPMFormLogs
          isOpen={modelViewRPMFormLogs}
          toggle={toggleViewRPMFormLogs}
          userId={userId}
          rpmSubmissinLogs={rpmSubmissinLogs}
        />
      )}

      {/* View RPM Care Plan Modal */}
      <Modal
        isOpen={showViewRPMModal}
        toggle={toggleViewRPMModal}
        size="lg"
        style={{ width: "50%" }}
        backdrop="static"
      >
        <ModalHeader className={CLASS_CLOSE_BUTTON} toggle={toggleViewRPMModal}>
          <Label className="align-items-center justify-content-center text-center"> View Remote Patient Monitoring Care Plan</Label>
          <div style={{ display: 'flex', float: 'right', gap: '10px' }}>
            {!isEditingRPM ? (
              <button
                type="button"
                onClick={handleUpdateRPMCarePlan}
                style={{ width: "230px" }}
                className="btn btn-info ml-2 update-click-cls">
                <i className="mdi mdi-pencil" style={{ fontSize: 20 }}></i> Update RPM Care Plan
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSaveRPMCarePlanEdit}
                style={{ width: "180px" }}
                className="btn btn-info ml-2 save-click-cls">
                <i className="mdi mdi-content-save" style={{ fontSize: 20 }}></i> Update Details
              </button>
            )}
            {!isEditingRPM && (
              <ReactToPrint
                trigger={() => (
                  <button
                    type="button"
                    style={{ width: "130px" }}
                    className="btn btn-info ml-2 print-click-cls">
                    <i className="mdi mdi-printer" style={{ fontSize: 20 }}></i> Print
                  </button>
                )}
                content={() => viewRPMRef.current ? viewRPMRef.current.getPrintRef() : null}
              />
            )}
            <button
              type="button"
              onClick={toggleViewRPMModal}            
              className="btn btn-info ml-2 close-click-cls">
              <i className="mdi mdi-close" style={{ fontSize: 20 }}></i> Close
            </button>
          </div>
        </ModalHeader>

        <ModalBody style={{ backgroundColor: "white" }}>
          <ViewRPMCarePlan 
            ref={viewRPMRef}
            userId={userId}
            rpmCarePlanId={selectedRPMCarePlan?._id}
            onCancel={toggleViewRPMModal}
            isEditing={isEditingRPM}
            onSaveComplete={handleSaveRPMCarePlanEdit}
          />
        </ModalBody>
      </Modal>

      {/* Add RPM Care Plan Modal */}
      <Modal
        isOpen={showAddRPMModal}
        toggle={toggleAddRPMModal}
        size="lg"
        style={{ width: "50%" }}
        backdrop="static"
      >
        <ModalHeader className={CLASS_CLOSE_BUTTON} toggle={toggleAddRPMModal}>
          <Label className="align-items-center justify-content-center text-center"> Add Remote Patient Monitoring Care Plan</Label>
          <div style={{ display: 'flex', float: 'right', gap: '10px' }}>
            <button
              type="button"
              onClick={handleSaveRPMCarePlan}
              style={{ width: "100px" }}
              className="btn btn-info ml-2 save-click-cls" >
              <i className="mdi mdi-content-save" style={{ fontSize: 20 }}></i> Save
            </button>
            <button
              type="button"
              onClick={toggleAddRPMModal}            
              className="btn btn-info ml-2 close-click-cls" >
              <i className="mdi mdi-close" style={{ fontSize: 20 }}></i> Close
            </button>
          </div>
        </ModalHeader>

        <ModalBody style={{ backgroundColor: "white" }}>
          <AddRPMCarePlan 
            patientId={userId} 
            onCancel={toggleAddRPMModal}
            onSubmitForm={handleSubmitForm}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default RPMCarePlan;
